// pages/overlay/Outro.jsx
// Closing credits screen — warm, personal, memorable. Shows what was made
// tonight, drives follows/tips/bookings. The emotional cap on the stream.
//
// Landscape: left ~45% sign-off + socials + tip CTA, right ~55% pancake gallery.
// Portrait (IG Live cut, 2026-07-03): the auto-scrolling pancake gallery IS
//   the screen — full-bleed background with feathered top/bottom masks — and
//   the sign-off card / rotating CTA / shift roundup float in front of it,
//   stacked inside the IG safe zone (y≈250–1320; see Main.jsx zone map). The
//   follow moment is the point: socials ride in the hero card. Perf: no
//   SparkleField in portrait (the drift is the motion), floating cards are
//   static translucent ink (no backdrop-filter, no animated box-shadows).
//
// The pancake gallery leads with the last active shift's finished pancakes —
// the running shift, or the one that just closed (operators often tap "Close
// shift" before switching here) — followed by the starred Library showpieces
// (`isShowcase`), deduped. Tiles animate in with a warm fade + scale.

import { useEffect, useRef, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "./../../firebase";
import useOrientation from "./../../hooks/useOrientation";
import useOverlayPalette from "./../../hooks/useOverlayPalette";
import useStreamConfig from "./../../hooks/useStreamConfig";
import useCurrentShiftOrders from "./../../hooks/useCurrentShiftOrders";
import useObsActive from "./../../hooks/useObsActive";
import JoPLogo from "./../../components/overlay/JoPLogo";
import SparkleField from "./../../components/overlay/SparkleField";

// ── GALLERY LOADER ──────────────────────────────────────────────────────────
const hasPhoto = (o) =>
  !!(o.photo_cutout_url || o.cutoutThumbnailUrl || o.photo_url || o.thumbnailUrl);
const isPublicShareable = (o) =>
  o.galleryPrivacy !== "private" && !o.hidden && !o.trashedAt;

// Only fall back to a closed shift if it ended this recently — never resurrect
// a different day's shift onto the outro.
const RECENT_SHIFT_WINDOW_MS = 12 * 60 * 60 * 1000;

function useOutroGallery(shiftLoaded, activeShift, shiftOrders) {
  const [showcaseOrders, setShowcaseOrders] = useState([]);
  const [fallbackShift, setFallbackShift] = useState(null);
  const [fallbackOrders, setFallbackOrders] = useState([]);

  // Curated showpieces — every pancake starred via the ⭐ in Library
  // (`isShowcase`). No arbitrary cap: the set is self-limiting by curation.
  // Single-equality query → no composite index needed.
  useEffect(() => {
    if (!shiftLoaded) { setShowcaseOrders([]); return; }
    const q = query(collection(db, "orders"), where("isShowcase", "==", true));
    return onSnapshot(q, (snap) => {
      setShowcaseOrders(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(o => hasPhoto(o) && isPublicShareable(o))
      );
    });
  }, [shiftLoaded]);

  // Just-closed-shift fallback. useCurrentShiftOrders only tracks
  // `isActive == true`, so tapping "Close shift" before switching to the outro
  // zeroes it out. When no shift is active, look up the most recently closed
  // one (open shifts have `closedAt: null`, which sorts last in desc order).
  useEffect(() => {
    if (!shiftLoaded || activeShift) { setFallbackShift(null); return; }
    let cancelled = false;
    getDocs(query(collection(db, "shifts"), orderBy("closedAt", "desc"), limit(1)))
      .then((snap) => {
        if (cancelled || snap.empty) return;
        const shift = { id: snap.docs[0].id, ...snap.docs[0].data() };
        const closedMs = shift.closedAt?.toMillis?.() ?? 0;
        if (Date.now() - closedMs <= RECENT_SHIFT_WINDOW_MS) setFallbackShift(shift);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [shiftLoaded, activeShift?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Orders for the fallback shift — live, so late photos still land post-close.
  useEffect(() => {
    if (!fallbackShift) { setFallbackOrders([]); return; }
    const q = query(collection(db, "orders"), where("shiftId", "==", fallbackShift.id));
    return onSnapshot(q, (snap) => {
      setFallbackOrders(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(
            o =>
              (o.state === "done" || o.state === "ready_to_deliver") &&
              hasPhoto(o) && !o.trashedAt
          )
      );
    });
  }, [fallbackShift?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Two sections: the shift's pancakes in queue order, then the curated
  // showpieces newest-first, deduped against the shift.
  const sourceOrders = activeShift
    ? shiftOrders.filter(
        o => (o.state === "done" || o.state === "ready_to_deliver") && hasPhoto(o)
      )
    : fallbackOrders;
  const shiftPancakes = [...sourceOrders].sort(
    (a, b) => (a.queueNumber ?? 0) - (b.queueNumber ?? 0)
  );
  const seen = new Set(shiftPancakes.map(o => o.id));
  const showpieces = showcaseOrders
    .filter(o => !seen.has(o.id))
    .sort(
      (a, b) =>
        (b.readyToDeliverAt?.toMillis?.() ?? 0) - (a.readyToDeliverAt?.toMillis?.() ?? 0)
    );

  return {
    shiftPancakes,
    showpieces,
    currentShiftId: activeShift?.id || fallbackShift?.id || null,
  };
}

export default function Outro() {
  useOverlayPalette();
  const orientation = useOrientation();
  const portrait = orientation === "portrait";
  const active = useObsActive();
  const config = useStreamConfig();
  const { activeShift, shiftLoaded, orders } = useCurrentShiftOrders();
  const { shiftPancakes, showpieces, currentShiftId } =
    useOutroGallery(shiftLoaded, activeShift, orders);

  // Gallery sections — "what we made today" then "the hall of fame". Each gets
  // a slim divider header; empty sections vanish entirely.
  const sections = [];
  if (shiftPancakes.length > 0)
    sections.push({ key: "shift", label: "Made Today", orders: shiftPancakes });
  if (showpieces.length > 0)
    sections.push({ key: "showpieces", label: "⭐ Showpieces", orders: showpieces });

  const outroMessage = config.outroMessage || "Thanks for watching!";
  const socials = config.featuredSocials || {};
  const tipUrl = config.tipJarUrl;
  void tipUrl;

  // ── Portrait: full-bleed drift background + floating sign-off stack ──
  if (portrait) {
    return (
      <div
        data-orientation={orientation}
        style={{
          width: "100vw", height: "100vh",
          background: "var(--jop-stage)",
          fontFamily: "system-ui, -apple-system, sans-serif",
          fontSize: "calc(18px + 0.7vw)",
          userSelect: "none", overflow: "hidden", position: "relative",
        }}
      >
        {/* Background — the gallery at full screen, feathered top + bottom */}
        {sections.length > 0 && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 1,
            display: "flex", flexDirection: "column",
            padding: "1rem 0",
            boxSizing: "border-box",
          }}>
            <GalleryGrid sections={sections} portrait active={active} bg />
          </div>
        )}

        {/* Floating stack — everything critical, inside the IG safe zone */}
        <div style={{
          position: "absolute", top: "250px", left: 0, right: 0,
          display: "flex", flexDirection: "column", alignItems: "center",
          gap: "1rem",
          zIndex: 2, pointerEvents: "none",
        }}>
          {/* Hero card — logo, sign-off, socials (the follow moment) */}
          <div style={{
            background: "rgba(var(--jop-ink-rgb), 0.86)",
            border: "3px solid var(--jop-gold)",
            borderRadius: "22px",
            boxShadow: "0 0 26px rgba(var(--jop-gold-rgb), 0.25)",
            padding: "1.5rem 2rem 1.4rem",
            width: "min(680px, calc(100vw - 160px))",
            boxSizing: "border-box",
            display: "flex", flexDirection: "column", alignItems: "center",
            gap: "0.9rem",
          }}>
            <JoPLogo variant="wide" height={130} animated />
            <div style={{
              color: "var(--jop-gold)",
              fontWeight: 900,
              fontSize: "1.9rem",
              textAlign: "center",
              lineHeight: 1.15,
              letterSpacing: "0.02em",
              textShadow: "0 2px 12px rgba(var(--jop-gold-rgb),0.4)",
            }}>
              {outroMessage}
            </div>
            <div style={{
              display: "flex", flexDirection: "column", gap: "0.5rem",
              width: "100%", maxWidth: "380px",
            }}>
              {socials.twitch && (
                <SocialRow icon="📺" label={socials.twitch} color="var(--jop-highlight-blue)" />
              )}
              {socials.instagram && (
                <SocialRow icon="📸" label={socials.instagram} color="var(--jop-pink)" />
              )}
              {socials.web && (
                <SocialRow icon="🌐" label={socials.web} color="var(--jop-cream)" />
              )}
            </div>
          </div>

          <RotatingCTA portrait active={active} solid />

          {currentShiftId && shiftPancakes.length > 0 && (
            <ShiftRoundup orders={shiftPancakes} solid />
          )}
        </div>

        <style>{`
          @keyframes jopOutroTipPulse {
            0%, 100% { transform: scale(1); }
            50%      { transform: scale(1.02); }
          }
          @keyframes jopOutroTileIn {
            0%   { opacity: 0; transform: scale(0.8); }
            100% { opacity: 1; transform: scale(1); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      data-orientation={orientation}
      style={{
        width: "100vw",
        height: "100vh",
        background: "var(--jop-stage)",
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: "calc(18px + 0.7vw)",
        userSelect: "none",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: portrait ? "column" : "row",
      }}
    >
      <SparkleField density={35} seed={77} />

      {/* ── LEFT / TOP: Sign-off panel ────────────────────────────────────── */}
      <div style={{
        flex: portrait ? "0 0 auto" : "0 0 42%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: portrait ? "flex-start" : "center",
        padding: portrait ? "1.5rem 1.5rem 1rem" : "2.5rem",
        position: "relative",
        zIndex: 2,
        gap: portrait ? "0.8rem" : "1.2rem",
      }}>
        <JoPLogo
          variant="wide"
          height={portrait ? 120 : 180}
          animated
        />

        {/* Sign-off message */}
        <div style={{
          color: "var(--jop-gold)",
          fontWeight: 900,
          fontSize: portrait ? "1.6rem" : "2.2rem",
          textAlign: "center",
          lineHeight: 1.15,
          letterSpacing: "0.02em",
          textShadow: "0 2px 12px rgba(var(--jop-gold-rgb),0.4)",
          maxWidth: "400px",
        }}>
          {outroMessage}
        </div>

        {/* Socials */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          marginTop: portrait ? "0.3rem" : "0.8rem",
          width: "100%",
          maxWidth: "320px",
        }}>
          {socials.twitch && (
            <SocialRow icon="📺" label={socials.twitch} color="var(--jop-highlight-blue)" />
          )}
          {socials.instagram && (
            <SocialRow icon="📸" label={socials.instagram} color="var(--jop-pink)" />
          )}
          {socials.web && (
            <SocialRow icon="🌐" label={socials.web} color="var(--jop-cream)" />
          )}
        </div>

        {/* Rotating CTA card */}
        <RotatingCTA portrait={portrait} active={active} />

        {/* Shift roundup — only when the current/just-closed shift has pancakes */}
        {currentShiftId && shiftPancakes.length > 0 && !portrait && (
          <ShiftRoundup orders={shiftPancakes} />
        )}
      </div>

      {/* ── RIGHT / BOTTOM: Pancake gallery ───────────────────────────────── */}
      <div style={{
        flex: 1,
        position: "relative",
        zIndex: 2,
        // Horizontal padding lives on the scroll container inside (GalleryGrid),
        // not here: `overflow: hidden` clips at the padding edge, so jittered
        // pancakes (transform-scaled past their tile) need that padding strip
        // as spill room — padding out here puts the clip line ~40px shy of the
        // panel edge and slices their sides off mid-screen.
        padding: portrait ? "0.5rem 0 1.5rem" : "2rem 0",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}>
        {/* Grid of pancake thumbnails — auto-sizes columns to fill space.
             ≤4 per row: tiles grow to fill. 5+ per row: starts scrolling. */}
        {sections.length > 0 ? (
          <GalleryGrid sections={sections} portrait={portrait} active={active} />
        ) : (
          <div style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(255,255,255,0.3)",
            fontSize: "1.1rem",
            fontStyle: "italic",
          }}>
            Gallery loading...
          </div>
        )}
      </div>

      {/* Portrait: stats at bottom */}
      {portrait && currentShiftId && shiftPancakes.length > 0 && (
        <div style={{ padding: "0.5rem 1rem", position: "relative", zIndex: 2 }}>
          <ShiftRoundup orders={shiftPancakes} />
        </div>
      )}

      <style>{`
        @keyframes jopOutroTipPulse {
          /* Scale-breath, not an animated box-shadow (a paint property that
             repaints every frame). transform composites for free. */
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.02); }
        }
        @keyframes jopOutroTileIn {
          0%   { opacity: 0; transform: scale(0.8); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

// ── ROTATING CTA ─────────────────────────────────────────────────────────────
const CTA_CARDS = [
  { icon: "🫙", line1: "Enjoyed the show?",           line2: "We FLIP for tips!",                      sub: "thejoyofpancakes.com" },
  { icon: "🎂", line1: "Yeah, we do parties.",         line2: "Book us for your next event",            sub: "thejoyofpancakes.com" },
  { icon: "🎟", line1: "Check your Golden Ticket",     line2: "Scan your ticket to see your finished pancake", sub: "thejoyofpancakes.com" },
  { icon: "✨", line1: "Preserve your pancake forever", line2: "Decoupage kits + full service",          sub: "thejoyofpancakes.com" },
];

// ── SHIFT ROUNDUP ───────────────────────────────────────────────────────────
// Shows stats for the current/just-closed shift only, not the showpieces.
// solid: opaque-ish ink background for floating over the portrait drift bg.
function ShiftRoundup({ orders: shiftOrders, solid = false }) {

  const total = shiftOrders.length;
  const custom = shiftOrders.filter(o => o.orderType === "custom" && !o.isFreestyle).length;
  const flash = shiftOrders.filter(o => o.orderType === "flash").length;
  const freestyle = shiftOrders.filter(o => o.isFreestyle).length;
  const magic = shiftOrders.filter(o => o.orderType === "magic").length;

  // Best draw time
  const drawTimes = shiftOrders.map(o => o.drawingTimeMs).filter(t => t != null && t > 0);
  const bestDraw = drawTimes.length > 0 ? Math.min(...drawTimes) : null;
  // Average total time
  const totalTimes = shiftOrders.map(o => (o.drawingTimeMs || 0) + (o.cookTimeMs || 0)).filter(t => t > 0);
  const avgTotal = totalTimes.length > 0 ? Math.round(totalTimes.reduce((a, b) => a + b, 0) / totalTimes.length) : null;

  function fmtDur(ms) {
    if (!ms) return null;
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
  }

  // Build type breakdown string
  const parts = [];
  if (custom > 0) parts.push(`${custom} custom`);
  if (flash > 0) parts.push(`${flash} flash`);
  if (freestyle > 0) parts.push(`${freestyle} freestyle`);
  if (magic > 0) parts.push(`${magic} magic`);

  return (
    <div style={{
      marginTop: solid ? 0 : "auto",
      background: solid ? "rgba(var(--jop-ink-rgb),0.86)" : "rgba(255,255,255,0.06)",
      border: solid ? "2px solid rgba(var(--jop-gold-rgb),0.45)" : "1px solid rgba(var(--jop-gold-rgb),0.2)",
      borderRadius: "14px",
      padding: "0.8rem 1rem",
      width: "100%",
      maxWidth: solid ? "420px" : "320px",
    }}>
      <div style={{
        color: "var(--jop-gold)",
        fontWeight: 900,
        fontSize: "1.5rem",
        textAlign: "center",
        marginBottom: "0.4rem",
      }}>
        {total} Pancake{total !== 1 ? "s" : ""} Today ✨
      </div>
      {parts.length > 0 && (
        <div style={{
          color: "rgba(255,255,255,0.55)",
          fontSize: "0.95rem",
          textAlign: "center",
          marginBottom: "0.3rem",
        }}>
          {parts.join(" · ")}
        </div>
      )}
      <div style={{
        display: "flex",
        justifyContent: "center",
        gap: "1rem",
        fontSize: "0.9rem",
        color: "rgba(255,255,255,0.45)",
      }}>
        {bestDraw && <span>⚡ Best draw: {fmtDur(bestDraw)}</span>}
        {avgTotal && <span>⏱ Avg: {fmtDur(avgTotal)}</span>}
      </div>
    </div>
  );
}

// solid: BILLBOARD mode for the portrait drift screen — these rotating cards
// are the outro's marketing voice, and they have to read from across a market
// (2000-nit IRL panel) AND on a phone-sized stream. Hero-card width, big
// display type, high-contrast ink background. Landscape keeps the compact
// side-column chip.
function RotatingCTA({ portrait, active = true, solid = false }) {
  const [idx, setIdx] = useState(0);
  const [vis, setVis] = useState(true);

  useEffect(() => {
    const t = setInterval(() => {
      setVis(false);
      setTimeout(() => {
        setIdx(i => (i + 1) % CTA_CARDS.length);
        setVis(true);
      }, 500);
    }, 8000);
    return () => clearInterval(t);
  }, []);

  const card = CTA_CARDS[idx];

  return (
    <div style={{
      marginTop: solid ? 0 : portrait ? "0.5rem" : "1rem",
      background: solid ? "rgba(var(--jop-ink-rgb),0.9)" : "rgba(var(--jop-gold-rgb),0.15)",
      border: solid ? "3px solid var(--jop-gold)" : "2px solid var(--jop-gold)",
      borderRadius: solid ? "20px" : "16px",
      padding: solid ? "1.2rem 2rem 1.3rem" : portrait ? "0.75rem 1.25rem" : "1rem 1.5rem",
      textAlign: "center",
      boxShadow: solid
        ? "0 0 24px rgba(var(--jop-gold-rgb),0.3)"
        : "0 0 14px 2px rgba(var(--jop-gold-rgb),0.22)",
      animation: "jopOutroTipPulse 2s ease-in-out infinite",
      animationPlayState: active ? "running" : "paused",
      maxWidth: solid ? "min(680px, calc(100vw - 160px))" : "320px",
      width: "100%",
      minHeight: solid ? "215px" : portrait ? "120px" : "140px",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      opacity: vis ? 1 : 0,
      transition: "opacity 0.5s ease",
      fontFamily: solid ? "var(--jop-display)" : "inherit",
    }}>
      <div style={{ fontSize: solid ? "2.6rem" : "1.8rem", marginBottom: "0.3rem" }}>{card.icon}</div>
      <div style={{
        color: "var(--jop-gold)",
        fontWeight: 900,
        fontSize: solid ? "2rem" : portrait ? "1.1rem" : "1.3rem",
        lineHeight: 1.15,
        textShadow: solid ? "0 2px 10px rgba(0,0,0,0.5)" : "none",
      }}>
        {card.line1}
      </div>
      <div style={{
        color: "white",
        fontWeight: 800,
        fontSize: solid ? "1.55rem" : portrait ? "1.1rem" : "1.3rem",
        marginTop: solid ? "0.35rem" : "0.2rem",
        lineHeight: 1.25,
      }}>
        {card.line2}
      </div>
      <div style={{
        color: solid ? "var(--jop-cream)" : "rgba(255,255,255,0.45)",
        opacity: solid ? 0.85 : 1,
        fontWeight: solid ? 700 : 400,
        fontSize: solid ? "1.2rem" : "0.9rem",
        marginTop: solid ? "0.45rem" : "0.3rem",
        letterSpacing: solid ? "0.03em" : "normal",
      }}>
        {card.sub}
      </div>
    </div>
  );
}

// ── SECTION HEADER ──────────────────────────────────────────────────────────
// Slim gold divider — rule, label, rule. The only chrome between sections.
function SectionHeader({ label }) {
  const rule = {
    flex: 1,
    height: "1px",
    background: "rgba(var(--jop-gold-rgb),0.3)",
  };
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "0.7rem",
      margin: "0.2rem 0.3rem 0.55rem",
    }}>
      <div style={rule} />
      <div style={{
        color: "var(--jop-gold)",
        fontWeight: 800,
        fontSize: "0.85rem",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        opacity: 0.9,
        whiteSpace: "nowrap",
      }}>
        {label}
      </div>
      <div style={rule} />
    </div>
  );
}

// ── GALLERY GRID ─────────────────────────────────────────────────────────────
// Renders the gallery sections ("Made Today", "⭐ Showpieces") as labeled
// grids. Auto-scrolls when content overflows. 3 columns, staggered rows.
// bg: full-bleed background mode (portrait) — deeper top/bottom feather so
// the drift fades under the floating cards' zone edges.
function GalleryGrid({ sections, portrait, active = true, bg = false }) {
  const containerRef = useRef(null);
  const frameRef = useRef(null);

  const total = sections.reduce((n, s) => n + s.orders.length, 0);
  const cols = total <= 2 ? Math.max(total, 1) : 3;
  const gap = portrait ? "0.5rem" : "0.6rem";

  // Auto-scroll: ease out from edges, cruise in the middle, ease in to far edge,
  // pause, then bounce back. Tracks float position to avoid subpixel rounding.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Don't run the auto-scroll RAF loop while this overlay is off-air. Unlike a
    // backgrounded browser tab, an off-air OBS source is NOT document-hidden, so
    // requestAnimationFrame keeps firing (and each frame scrolls + repaints).
    // Gating on `active` stops that wasted work; it resumes on the next on-air.
    if (!active) return;
    const checkTimer = setTimeout(() => {
      if (el.scrollHeight <= el.clientHeight + 10) return;

      const CRUISE = 0.8;        // px/frame at full speed (~48px/sec)
      const EASE_ZONE = 60;      // px from each edge where we ramp speed
      const PAUSE_FRAMES = 150;  // ~2.5s pause at top/bottom
      let pos = 0;
      let dir = 1;
      let pauseCounter = PAUSE_FRAMES;

      function tick() {
        const maxScroll = el.scrollHeight - el.clientHeight;
        if (maxScroll <= 0) { frameRef.current = requestAnimationFrame(tick); return; }

        if (pauseCounter > 0) {
          pauseCounter--;
          frameRef.current = requestAnimationFrame(tick);
          return;
        }

        // Distance from the nearest edge
        const distFromEdge = dir > 0
          ? Math.min(pos, maxScroll - pos)
          : Math.min(pos, maxScroll - pos);
        // Ramp: 0 at edge → 1 at EASE_ZONE px in. Smoothstep for natural feel.
        const t = Math.min(distFromEdge / EASE_ZONE, 1);
        const smooth = t * t * (3 - 2 * t); // smoothstep
        const speed = CRUISE * (0.25 + 0.75 * smooth); // starts at 25% cruise speed

        pos += speed * dir;

        if (pos >= maxScroll) { pos = maxScroll; dir = -1; pauseCounter = PAUSE_FRAMES; }
        if (pos <= 0) { pos = 0; dir = 1; pauseCounter = PAUSE_FRAMES; }

        el.scrollTop = Math.round(pos);
        frameRef.current = requestAnimationFrame(tick);
      }
      frameRef.current = requestAnimationFrame(tick);
    }, 500);

    return () => { clearTimeout(checkTimer); cancelAnimationFrame(frameRef.current); };
  }, [total, active]);

  // Running tile index for staggered animation
  let tileIdx = 0;

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        overflow: "hidden",
        // This padding doubles as spill room: overflow clips at the padding
        // edge, so transform-scaled pancakes can hang past their tiles into
        // this strip without getting truncated at an invisible line. Keep it
        // ≥ the worst-case jitter overhang (~15% of a tile width per side).
        padding: portrait ? "0 1.75rem" : "0 2.5rem 0 1.75rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.8rem",
        alignContent: "start",
        maskImage: bg
          ? "linear-gradient(to bottom, transparent 0%, black 9%, black 91%, transparent 100%)"
          : "linear-gradient(to bottom, transparent 0%, black 4%, black 96%, transparent 100%)",
        WebkitMaskImage: bg
          ? "linear-gradient(to bottom, transparent 0%, black 9%, black 91%, transparent 100%)"
          : "linear-gradient(to bottom, transparent 0%, black 4%, black 96%, transparent 100%)",
      }}
    >
      {sections.map((section) => {
        const startIdx = tileIdx;
        tileIdx += section.orders.length;

        return (
          <div key={section.key}>
            <SectionHeader label={section.label} />
            <div style={{
              display: "grid",
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gap,
              justifyItems: "center",
            }}>
              {section.orders.map((order, i) => (
                <PancakeTile key={order.id} order={order} index={startIdx + i} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── SOCIAL ROW ───────────────────────────────────────────────────────────────
function SocialRow({ icon, label, color }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "0.65rem",
      background: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.12)",
      borderRadius: "10px",
      padding: "0.5rem 0.85rem",
    }}>
      <span style={{ fontSize: "1.25rem", flexShrink: 0 }}>{icon}</span>
      <span style={{
        color,
        fontWeight: 700,
        fontSize: "1.1rem",
        letterSpacing: "0.02em",
      }}>{label}</span>
    </div>
  );
}

// ── PANCAKE TILE ─────────────────────────────────────────────────────────────
// Frameless — cutout PNG floats in space with a drop shadow. No container box.

// Deterministic per-pancake size jitter (1.06–1.30, seeded by order id). The
// cutouts' natural trim variance already reads organic but clusters unevenly;
// this scatters big/small across the grid and lifts the whole baseline. Scale
// is transform-only: composites on the GPU, never reflows the grid.
function sizeJitter(id = "") {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return 1.06 + (((h >>> 0) % 1000) / 1000) * 0.24;
}

function PancakeTile({ order, index }) {
  const src = order.cutoutThumbnailUrl || order.photo_cutout_url || order.thumbnailUrl || order.photo_url;
  const scale = sizeJitter(order.id);

  return (
    <div style={{
      animation: `jopOutroTileIn 0.5s ease-out ${Math.min(index, 20) * 0.08}s both`,
      padding: "0.3rem",
      width: "100%",
      aspectRatio: "1 / 1",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
    }}>
      {src ? (
        <PancakeImg src={src} scale={scale} />
      ) : (
        <span style={{ color: "rgba(255,255,255,0.15)", fontSize: "2rem" }}>🥞</span>
      )}
    </div>
  );
}

// Eager-loaded on purpose — `loading="lazy"` meant below-the-fold thumbnails
// didn't start fetching until the auto-scroll carried them on screen, so the
// bottom row popped in fully visible instead of riding the edge-mask fade.
// All thumbs now fetch during the initial top pause; any straggler still
// fades in on its own load event (one-shot opacity transition, composited).
function PancakeImg({ src, scale }) {
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);
  // onLoad never fires for cache-complete images mounted from a snapshot
  // re-render — catch those here.
  useEffect(() => {
    if (ref.current?.complete) setLoaded(true);
  }, []);

  return (
    <img
      ref={ref}
      src={src}
      alt=""
      decoding="async"
      onLoad={() => setLoaded(true)}
      style={{
        maxWidth: "100%",
        maxHeight: "100%",
        objectFit: "contain",
        transform: `scale(${scale})`,
        filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.5))",
        opacity: loaded ? 1 : 0,
        transition: "opacity 0.45s ease",
      }}
    />
  );
}
